import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data?.detail || "Check your inbox for a reset link.");
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bg-base)]">
      <div className="flex items-center justify-center p-8 fade-up">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <img src="/logo-talmia-ii.svg" alt="Talmia II" className="w-14 h-14 rounded-xl object-cover" />
            <span className="font-display text-xl font-bold">Talmia<span className="text-orange-400">II</span></span>
          </Link>

          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-2 tracking-tight">Forgot password</h1>
          <p className="text-slate-400 mb-8">
            Enter the email you use to sign in. If we find an account we&rsquo;ll send a reset link that&rsquo;s valid for one hour.
          </p>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">Email</label>
                <input
                  type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-dark mt-1.5" placeholder="you@company.com" data-testid="forgot-email-input"
                />
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="forgot-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading} className="btn-coral w-full mt-2" data-testid="forgot-submit">
                {loading ? "Sending link…" : "Send reset link"}
              </button>
            </form>
          ) : (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="forgot-success">
              <div className="font-semibold text-emerald-300 mb-1">Link sent</div>
              <p className="text-sm text-slate-300 leading-relaxed">{message}</p>
              <p className="text-xs text-slate-500 mt-3">Not seeing it? Give it 60 seconds, check spam, and make sure you used the address linked to your Talmia account.</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="link-back-to-login">
              ← Back to sign in
            </Link>
            <Link to="/register" className="text-slate-400 hover:text-slate-200" data-testid="link-to-register">
              Create an account
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-slate-900 to-orange-900/30" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,104,32,0.28) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(90,79,207,0.30) 0%, transparent 50%)" }} />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            One click and you&rsquo;re back in.
          </div>
          <p className="text-slate-400 mt-3 max-w-md">Reset links expire after 60 minutes and can be used only once — for your security.</p>
        </div>
      </div>
    </div>
  );
}
